import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, EmptyState, Shimmer, useToast } from '@teals/ui-web';
import { useAuth } from '../../hooks';
import {
  useAddApplicationWorkflowMutation,
  useGetApplicationWorkflowsQuery,
  useGetWorkflowsQuery,
  useRemoveApplicationWorkflowMutation,
  workflowErrorMessage,
} from '../../store/api';

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
`;

const Add = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const Select = styled.select`
  flex: 1;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--bg-surface);
  color: var(--text-primary);
`;

const Note = styled.p`
  margin: 0 0 12px;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const ApplicationWorkflows: React.FC<{ appId: string }> = ({ appId }) => {
  const { show } = useToast();
  const { user } = useAuth();
  const orgId = user?.organizationId || '';
  const { data: linked = [], isLoading } = useGetApplicationWorkflowsQuery(appId);
  const { data: all = [] } = useGetWorkflowsQuery(orgId, { skip: !orgId });
  const [add, { isLoading: adding }] = useAddApplicationWorkflowMutation();
  const [remove] = useRemoveApplicationWorkflowMutation();
  const [choice, setChoice] = useState('');
  const available = all.filter(
    (item) => item.lifecycle !== 'archived' && !linked.some((row) => row.id === item.id),
  );

  const addChosen = async () => {
    if (!choice) return;
    try {
      await add({ appId, workflowId: choice }).unwrap();
      setChoice('');
      show({ message: 'Added. This application can now start it.', type: 'success' });
    } catch (error) {
      show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  const removeRow = async (workflowId: string) => {
    try {
      await remove({ appId, workflowId }).unwrap();
      show({
        message: 'Removed. New starts are refused, and runs already going finish.',
        type: 'success',
      });
    } catch (error) {
      show({ message: workflowErrorMessage(error), type: 'error' });
    }
  };

  if (isLoading) return <Shimmer height="120px" />;
  return (
    <Card padding={20} elevation="sm">
      <Note>
        Workflows this application can start from the API. A run it starts counts against this
        application's budget, and its allowed models apply.
      </Note>
      <Add>
        <Select
          aria-label="Workflow to add"
          value={choice}
          onChange={(event) => setChoice(event.target.value)}
        >
          <option value="">Choose a workflow</option>
          {available.map((item) => (
            <option key={item.id} value={item.id}>
              {item.title || item.name}
            </option>
          ))}
        </Select>
        <Button onClick={addChosen} disabled={!choice} isLoading={adding}>
          Add
        </Button>
      </Add>
      {linked.length === 0 ? (
        <EmptyState
          title="No workflows yet"
          description="Add a workflow to let this application start it."
        />
      ) : (
        linked.map((item) => (
          <Row key={item.id}>
            <div>
              <Link to={`/workflows/${item.id}`}>{item.title || item.name}</Link>{' '}
              <Badge variant={item.lifecycle === 'active' ? 'success' : 'neutral'} size="sm">
                {item.lifecycle}
              </Badge>
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeRow(item.id)}>
              Remove
            </Button>
          </Row>
        ))
      )}
    </Card>
  );
};

export default ApplicationWorkflows;
